import React from "react";
import { Check } from "lucide-react";
import { clsx } from "clsx";

import { Progress } from "@/design-system/components/Progress/Progress";
import { Typography } from "@/design-system/components/Typography/Typography";
import { getStepProgress } from "./navigation";
import { AnyWizardDefinition, WizardMascot, WizardSession } from "./types";

type WizardStepRailProps = {
  mascot?: WizardMascot;
  steps: AnyWizardDefinition["steps"];
  session: WizardSession;
  currentStepId: string;
  onSelectStep: (stepId: string) => void;
};

export function WizardStepRail({
  mascot,
  steps,
  session,
  currentStepId,
  onSelectStep,
}: WizardStepRailProps) {
  const isCompleted = session.completedAt !== null;
  const isDone = (stepId: string) =>
    isCompleted || session.completedStepIds.includes(stepId);
  const { done: doneCount } = getStepProgress(steps, session);
  const firstIncompleteIndex = steps.findIndex((step) => !isDone(step.id));

  return (
    <div className="border-borderSubtle flex w-80 shrink-0 flex-col gap-4 overflow-auto border-r px-4 py-6">
      {mascot && (
        <WizardGuide
          mascot={mascot}
          isCompact={isCompleted || currentStepId !== steps[0]?.id}
        />
      )}
      <nav aria-label="Wizard steps" className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 px-3">
          <div className="flex items-baseline justify-between gap-2">
            <Typography variant="label-sm">Progress</Typography>
            <Typography variant="label-xs" color="subtle">
              {doneCount} of {steps.length}{" "}
              {steps.length === 1 ? "step" : "steps"}
            </Typography>
          </div>
          <div aria-hidden>
            <Progress
              value={doneCount}
              max={steps.length}
              className="bg-surface-muted h-1.5"
              indicatorClassName="bg-action-primary duration-500"
            />
          </div>
        </div>
        <ol className="flex flex-col gap-1">
          {steps.map((step, index) => {
            const done = isDone(step.id);
            const active = !isCompleted && step.id === currentStepId;
            const reachable =
              !isCompleted &&
              (done ||
                firstIncompleteIndex === -1 ||
                index <= firstIncompleteIndex);
            const startsPhase =
              !!step.phase && step.phase !== steps[index - 1]?.phase;
            return (
              <li key={step.id} className="flex flex-col">
                {startsPhase && (
                  <span
                    className={clsx(
                      "text-label-xs text-textSubtle px-3 pb-1",
                      index > 0 && "pt-3",
                    )}
                  >
                    {step.phase}
                  </span>
                )}
                <button
                  type="button"
                  disabled={!reachable}
                  aria-current={active ? "step" : undefined}
                  onClick={() => onSelectStep(step.id)}
                  className={clsx(
                    "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                    reachable ? "cursor-pointer" : "cursor-not-allowed",
                    active
                      ? "bg-surface-selected"
                      : reachable && "hover:bg-surface-hover",
                  )}
                >
                  <span
                    className={clsx(
                      "text-label-xs flex size-6 shrink-0 items-center justify-center rounded-full transition-colors",
                      done
                        ? "bg-action-primary text-action-primary-text"
                        : "border-borderDefault bg-surface-default text-textSubtle border",
                    )}
                  >
                    {done ? (
                      <Check
                        className="size-3.5"
                        strokeWidth={2.5}
                        aria-hidden
                      />
                    ) : (
                      index + 1
                    )}
                  </span>
                  <span
                    className={clsx(
                      "text-label-sm truncate",
                      done ? "text-textSubtle" : "text-textPrimary",
                    )}
                  >
                    {step.title}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
}

function WizardGuide({
  mascot,
  isCompact,
}: {
  mascot: WizardMascot;
  isCompact: boolean;
}) {
  if (isCompact) {
    return (
      <div className="flex items-center gap-2 px-3">
        <mascot.Art className="size-6" />
        <Typography variant="label-xs" className="text-textTertiary truncate">
          Guided by {mascot.name}
        </Typography>
      </div>
    );
  }
  return (
    <div className="bg-surface-sunken flex items-center gap-3 rounded-lg p-3">
      <mascot.Art className="size-14" />
      <div className="flex min-w-0 flex-col gap-0.5">
        <Typography variant="eyebrow-xs">Your guide</Typography>
        <Typography variant="label-sm">{mascot.name}</Typography>
        <Typography variant="body-xs" className="text-textTertiary">
          {mascot.tagline}
        </Typography>
      </div>
    </div>
  );
}
